'use client'

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

import type { ProductCardProps } from './ProductCard'
import { ProductCard } from './ProductCard'

const CARD_WIDTH = 260
const CARD_GAP = 12
const MARQUEE_SPEED = 0.6 // pixels per frame (~36px/s at 60fps)

/**
 * Same one-line infinite horizontal scrolling carousel as homepage ProductCarousel,
 * with snap and dot indicators, but renders ProductCard and uses fixed card size.
 * Optional heading shown top-left with dot nav top-right per design.
 * When marquee is true, carousel auto-scrolls continuously (pauses on hover and on pointer/touch).
 */
export function ProductsPageCarousel({
  products,
  heading = 'OIL FILTERS',
  marquee = false,
  locale,
}: {
  products: ProductCardProps[]
  heading?: string
  marquee?: boolean
  locale: AppLocale
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(0)
  const jumpingRef = useRef(false)
  const [hasInitialScroll, setHasInitialScroll] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const scrollStateRef = useRef({ cardIndex: 0, offset: 0 })
  const rafRef = useRef<number | undefined>(undefined)
  const marqueePauseUntilRef = useRef(0)
  const isHoveredRef = useRef(false)

  const n = products.length
  const list = n === 0 ? [] : [...products, ...products, ...products]
  const cardStep = CARD_WIDTH + CARD_GAP
  const setWidth = n * cardStep
  const spacer = Math.max(0, containerWidth - CARD_WIDTH)
  const isReady = n > 0

  useLayoutEffect(() => {
    const el = containerRef.current
    if (!el) return
    const ro = new ResizeObserver(() => setContainerWidth(el.clientWidth))
    ro.observe(el)
    setContainerWidth(el.clientWidth)
    return () => ro.disconnect()
  }, [])

  const recenter = useCallback(() => {
    const el = scrollRef.current
    if (!el || jumpingRef.current || n === 0) return
    const x = el.scrollLeft
    const cardIndex = Math.floor(x / cardStep)
    scrollStateRef.current = { cardIndex: cardIndex % n, offset: x % cardStep }
    const currentSet = Math.floor(x / setWidth)
    if (currentSet === 0 || currentSet === 2) {
      const offsetInSet = ((cardIndex % n) + n) % n
      const targetScroll = setWidth + offsetInSet * cardStep + (x % cardStep)
      jumpingRef.current = true
      const prevBehavior = el.style.scrollBehavior
      el.style.scrollBehavior = 'auto'
      el.scrollLeft = targetScroll
      el.style.scrollBehavior = prevBehavior
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          jumpingRef.current = false
        })
      })
    }
  }, [n, setWidth, cardStep])

  useLayoutEffect(() => {
    const el = scrollRef.current
    if (!el || !isReady || n === 0) return
    if (!hasInitialScroll) {
      const { cardIndex, offset } = scrollStateRef.current
      const targetIndex = Math.min(cardIndex, n - 1)
      el.scrollLeft = setWidth + targetIndex * cardStep + offset
      setActiveIndex(targetIndex)
      setHasInitialScroll(true)
    }
  }, [isReady, n, setWidth, cardStep, hasInitialScroll])

  useEffect(() => {
    const el = scrollRef.current
    if (!el || n === 0 || !hasInitialScroll) return
    let isScrolling = false
    const monitorScroll = () => {
      if (isScrolling && !jumpingRef.current) {
        recenter()
        setActiveIndex(scrollStateRef.current.cardIndex)
        rafRef.current = requestAnimationFrame(monitorScroll)
      }
    }
    const handleScroll = () => {
      if (jumpingRef.current) return
      if (!isScrolling) {
        isScrolling = true
        rafRef.current = requestAnimationFrame(monitorScroll)
      }
    }
    const handleScrollEnd = () => {
      isScrolling = false
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      recenter()
      setActiveIndex(scrollStateRef.current.cardIndex)
    }
    el.addEventListener('scroll', handleScroll, { passive: true })
    if ('onscrollend' in el) el.addEventListener('scrollend', handleScrollEnd as EventListener)
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      el.removeEventListener('scroll', handleScroll)
      if ('onscrollend' in el) el.removeEventListener('scrollend', handleScrollEnd as EventListener)
    }
  }, [n, setWidth, recenter, hasInitialScroll])

  // Marquee: auto-scroll when not interacting
  useEffect(() => {
    if (!marquee || n === 0 || !hasInitialScroll) return
    const el = scrollRef.current
    if (!el) return
    let raf: number
    const tick = () => {
      const now = Date.now()
      if (now < marqueePauseUntilRef.current || isHoveredRef.current) {
        raf = requestAnimationFrame(tick)
        return
      }
      if (!jumpingRef.current) {
        el.scrollLeft += MARQUEE_SPEED
        recenter()
        setActiveIndex(scrollStateRef.current.cardIndex)
      }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [marquee, n, hasInitialScroll, recenter])

  const snap = useCallback(() => {
    const el = scrollRef.current
    if (!el || jumpingRef.current || list.length === 0) return
    const x = el.scrollLeft
    const i = Math.round(x / cardStep)
    const target = i * cardStep
    if (Math.abs(x - target) > 2) el.scrollLeft = target
  }, [cardStep, list.length])

  useEffect(() => {
    if (marquee) return // no snap when marquee is running
    const el = scrollRef.current
    if (!el || !hasInitialScroll) return
    let t: ReturnType<typeof setTimeout>
    const onStop = () => {
      clearTimeout(t)
      t = setTimeout(snap, 100)
    }
    el.addEventListener('scroll', onStop, { passive: true })
    if ('onscrollend' in el) el.addEventListener('scrollend', snap as EventListener)
    return () => {
      clearTimeout(t)
      el.removeEventListener('scroll', onStop)
      if ('onscrollend' in el) el.removeEventListener('scrollend', snap as EventListener)
    }
  }, [marquee, snap, hasInitialScroll])

  const scrollToIndex = useCallback(
    (index: number, instant = false) => {
      const el = scrollRef.current
      if (!el || index < 0 || index >= n) return
      const left = setWidth + index * cardStep
      el.scrollTo({ left, behavior: instant ? 'auto' : 'smooth' })
    },
    [n, setWidth, cardStep],
  )

  const CARDS_PER_DOT = 5
  const PAGINATION_DOTS = Math.min(10, Math.max(1, Math.ceil(n / CARDS_PER_DOT)))
  const activeDotIndex = Math.min(PAGINATION_DOTS - 1, Math.floor(activeIndex / CARDS_PER_DOT))
  const scrollToDot = useCallback(
    (dotIndex: number) => {
      const targetIndex = Math.min(n - 1, dotIndex * CARDS_PER_DOT)
      scrollToIndex(targetIndex, true)
    },
    [n, scrollToIndex],
  )

  if (n === 0) {
    return (
      <div className="py-12 text-center font-poppins text-neutral-600">
        No products Yet. Search to Discover Available Products.
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className="relative w-full"
      role="region"
      aria-label="Products carousel"
    >
      <div className="mx-auto w-full max-w-5xl px-4 pb-3 sm:px-6 lg:px-8">
        <div  dir={locale === 'ar' ? 'rtl' : 'ltr'} className="flex items-center justify-between">
          <h2 className="font-bebas-neue font-bold uppercase text-[49.54px] leading-[54.49px] tracking-[1px] text-[#111]">
            {heading}
          </h2>
          <div className="flex items-center gap-1.5" role="tablist" aria-label="Carousel position">
            {Array.from({ length: PAGINATION_DOTS }).map((_, index) => (
              <button
                key={index}
                type="button"
                role="tab"
                aria-selected={activeDotIndex === index}
                aria-label={`Go to section ${index + 1} of ${PAGINATION_DOTS}`}
                onClick={(e) => {
                  e.preventDefault()
                  scrollToDot(index)
                }}
                className="h-2 w-2 shrink-0 cursor-pointer rounded-full border border-[#111] bg-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF0000] focus-visible:ring-offset-2 [&[aria-selected=true]]:border-[#111] [&[aria-selected=true]]:bg-[#111]"
                style={{ minWidth: 8, minHeight: 8 }}
              />
            ))}
          </div>
        </div>
      </div>
      <div
        ref={scrollRef}
        className={`overflow-x-auto overflow-y-hidden touch-manipulation ${marquee ? '' : 'scroll-smooth snap-x snap-mandatory'}`}
        style={{ WebkitOverflowScrolling: 'touch' }}
        tabIndex={0}
        onMouseEnter={() => {
          if (marquee) isHoveredRef.current = true
        }}
        onMouseLeave={() => {
          if (marquee) isHoveredRef.current = false
        }}
        onPointerDown={() => {
          if (marquee) marqueePauseUntilRef.current = Date.now() + 2500
        }}
        onTouchStart={() => {
          if (marquee) marqueePauseUntilRef.current = Date.now() + 2500
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft') {
            e.preventDefault()
            scrollRef.current?.scrollBy({ left: -cardStep, behavior: 'smooth' })
          } else if (e.key === 'ArrowRight') {
            e.preventDefault()
            scrollRef.current?.scrollBy({ left: cardStep, behavior: 'smooth' })
          }
        }}
      >
        <div className="flex pb-4 pt-4" style={{ gap: CARD_GAP }}>
          {list.map((item, i) => (
            <div
              key={i}
              className="flex-shrink-0 snap-start snap-always"
              style={{
                width: CARD_WIDTH,
                minWidth: CARD_WIDTH,
                scrollSnapAlign: 'start',
                scrollSnapStop: 'always',
              }}
            >
              <ProductCard {...item} />
            </div>
          ))}
          {spacer > 0 && (
            <div
              className="flex-shrink-0"
              style={{ width: spacer, minWidth: spacer }}
              aria-hidden
            />
          )}
        </div>
      </div>
    </div>
  )
}
